import type {
  ServerTraceDiagnosticsFailureSummary,
  ServerTraceDiagnosticsLogEvent,
  ServerTraceDiagnosticsResult,
  ServerTraceDiagnosticsSpanOccurrence,
} from "@cmmd-forge/contracts";

import { cn } from "../../lib/utils";
import {
  DiagnosticsCard,
  DiagnosticsSection,
  StatusPill,
  formatTimestamp,
} from "./DiagnosticsPanelPrimitives";
import {
  firstCauseLine,
  formatDurationMs,
  formatTraceWindow,
  traceCoverageWarning,
  traceEvidenceState,
  traceHeadline,
  traceSummaryStats,
} from "./DiagnosticsTraceFailures.logic";

function SummaryStrip({ result }: { readonly result: ServerTraceDiagnosticsResult }) {
  const window = formatTraceWindow(result);
  return (
    <div className="border-b border-border/65 px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        {traceSummaryStats(result).map((stat) => (
          <div key={stat.label} className="flex items-baseline gap-2">
            <span
              className={cn(
                "font-mono text-lg tabular-nums",
                stat.tone === "error" ? "text-destructive-foreground" : null,
                stat.tone === "warning" ? "text-warning-foreground" : null,
                stat.tone === "muted" ? "text-foreground" : null,
              )}
            >
              {stat.value}
            </span>
            <span className="text-[11px] text-muted-foreground">{stat.label}</span>
          </div>
        ))}
      </div>
      {window === null ? null : (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Spanning {window}, ending {formatTimestamp(result.lastSpanAt)}.
        </p>
      )}
    </div>
  );
}

function SectionHeading({ title, hint }: { readonly title: string; readonly hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/65 bg-muted/20 px-4 py-2">
      <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
        {title}
      </span>
      {hint === undefined ? null : (
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      )}
    </div>
  );
}

function FailureRow({ failure }: { readonly failure: ServerTraceDiagnosticsFailureSummary }) {
  return (
    <div className="border-b border-border/45 px-4 py-2.5 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate font-medium text-foreground">{failure.name}</span>
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          ×{failure.count}
        </span>
      </div>
      <p className="mt-1 truncate font-mono text-xs text-destructive-foreground">
        {firstCauseLine(failure.cause)}
      </p>
      <p className="mt-1 font-mono text-[11px] text-muted-foreground">
        last {formatTimestamp(failure.lastSeenAt)} · trace {failure.traceId.slice(0, 12)}
      </p>
    </div>
  );
}

function LogRow({ event }: { readonly event: ServerTraceDiagnosticsLogEvent }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-border/45 px-4 py-2 last:border-b-0">
      <span className="w-14 shrink-0 font-mono text-[11px] uppercase text-muted-foreground">
        {event.level}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs text-foreground">{event.message}</span>
      <span className="shrink-0 truncate font-mono text-[11px] text-muted-foreground">
        {event.spanName}
      </span>
      <span className="shrink-0 text-[11px] text-muted-foreground">
        {formatTimestamp(event.seenAt)}
      </span>
    </div>
  );
}

function SlowSpanRow({ span }: { readonly span: ServerTraceDiagnosticsSpanOccurrence }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-border/45 px-4 py-2 last:border-b-0">
      <span className="min-w-0 flex-1 truncate text-xs text-foreground">{span.name}</span>
      <span className="shrink-0 font-mono text-xs tabular-nums text-foreground">
        {formatDurationMs(span.durationMs)}
      </span>
      <span className="shrink-0 text-[11px] text-muted-foreground">
        {formatTimestamp(span.endedAt)}
      </span>
    </div>
  );
}

/**
 * The trace file has always been on disk; nothing read it back. This turns it
 * into the one thing a diagnostics page should lead with — what has been
 * failing — instead of another table of identifiers.
 */
export function DiagnosticsTraceFailures({
  result,
  error,
  pending,
  unsupportedReason,
}: {
  readonly result: ServerTraceDiagnosticsResult | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
  /** Why this target has no trace read at all, rather than an empty one. */
  readonly unsupportedReason?: string | undefined;
}) {
  if (unsupportedReason !== undefined) {
    return <DiagnosticsSection title="Traced failures" description={unsupportedReason} />;
  }
  if (pending && result === undefined) {
    return <DiagnosticsSection title="Traced failures" description="Reading the trace file…" />;
  }
  if (result === undefined) {
    return (
      <DiagnosticsSection
        title="Traced failures"
        description={error ?? "This runtime did not return trace diagnostics."}
      />
    );
  }

  const state = traceEvidenceState(result);
  const coverage = traceCoverageWarning(result);
  const hasRecords = result.recordCount > 0;
  return (
    <DiagnosticsSection
      title="Traced failures"
      id="diagnostics-traces"
      description={traceHeadline(result)}
      action={coverage ? <StatusPill label={coverage.label} tone={coverage.tone} /> : null}
    >
      <DiagnosticsCard>
        {hasRecords ? <SummaryStrip result={result} /> : null}

        {result.error === undefined ? null : (
          <p className="border-b border-border/65 px-4 py-2.5 text-xs text-muted-foreground">
            {result.error.message}
          </p>
        )}

        {result.commonFailures.length > 0 ? (
          <>
            <SectionHeading title="Repeated failures" hint="grouped by span and cause" />
            {result.commonFailures.map((failure) => (
              <FailureRow key={`${failure.name}-${failure.cause}`} failure={failure} />
            ))}
          </>
        ) : null}

        {result.latestWarningAndErrorLogs.length > 0 ? (
          <>
            <SectionHeading title="Recent warnings and errors" hint="newest first" />
            {result.latestWarningAndErrorLogs.map((event) => (
              <LogRow key={`${event.spanId}-${event.seenAt}-${event.message}`} event={event} />
            ))}
          </>
        ) : null}

        {result.slowestSpans.length > 0 ? (
          <>
            <SectionHeading title="Slowest spans" />
            {result.slowestSpans.map((span) => (
              <SlowSpanRow key={`${span.spanId}-${span.endedAt}`} span={span} />
            ))}
          </>
        ) : null}

        {hasRecords ? null : (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {state === "unavailable"
              ? "Tracing writes to this path once the server handles its first request."
              : "Nothing has been traced yet."}
          </p>
        )}
        <p className="border-t border-border/65 px-4 py-2 font-mono text-[11px] text-muted-foreground">
          {result.traceFilePath}
          {result.scannedFilePaths.length > 1
            ? ` (+${result.scannedFilePaths.length - 1} rotated)`
            : null}
        </p>
      </DiagnosticsCard>
    </DiagnosticsSection>
  );
}
